import CardCommon from '@op/components/CardCommon';
import {
  tableIcon,
  lineIcon,
  barIcon,
  vsIcon,
  pieIcon,
  stackLineIcon,
  SymbolType,
} from '@op/constants/chart';
import { Col, Row } from 'tea-component';
import React, { useMemo, useState } from 'react';
import {
  getDefaultReview,
} from '@op/pages/gamestore/hooks/useGameStorePcConsole';
import usePcConsoleTimelineChart from '@op/pages/gamestore/hooks/usePcConsoleTimelineChart';
import { useHistory } from 'umi';
import { goToCompetitorKeyMetrics } from '@op/utils/url';
import { arrayToSimpleObj, sortTimeListFunc } from '@op/utils';
import { downloadAction } from '@op/pages/download/utils';
import { formatDateFunc } from '@op/utils/time';
import { decimalFormatPercentage } from '@op/utils/value';
import MentionsTable from '@op/pages/Overview/KeyMetrics/GameStore/Steam/MentionsTable';
import MentionsPieCharts from '@op/pages/Overview/KeyMetrics/GameStore/Steam/MentionsPieCharts';
import MentionsLineBarCharts from '@op/pages/Overview/KeyMetrics/GameStore/Steam/MentionsLineBarCharts';
import ReviewsProgress from '@op/pages/Overview/KeyMetrics/GameStore/Steam/ReviewsProgress';
import './index.scss';
import { useGameStorePcConsole } from './hooks/useGameStorePcConsole';
import Ratings from './Ratings';

function getTableColumns(chartType: SymbolType) {
  const common: Array<any> = [
    {
      text: 'Positive',
      key: 'select_recommend',
      type: chartType,
      color: '#69D19C',
      valueKey: 'review',
    },
    {
      text: 'Negative',
      key: 'select_non_recommend',
      type: chartType,
      color: '#F17C86',
      valueKey: 'review',
    },
  ];

  if (chartType === 'bar' || chartType === 'table') {
    common.unshift({
      text: '30-Day Positive %',
      key: 'recent_reviews',
      type: 'line',
      color: '#5086F3',
      valueKey: 'score',
      numFmt: ['0%'],
      format: (value: number) => decimalFormatPercentage(value, '-', 0),
    });
  }

  return common;
}

export default function OpinionsGameStore({
  filter,
  // wholeGameInfo,
  // gameInfo,
}: {
  filter: any;
  wholeGameInfo: any;
  gameInfo: any;
}) {
  const [chartType, setChartType] = useState<SymbolType>('bar');
  function changeChartType(type: any) {
    if (type === 'vs') {
      goToCompetitorKeyMetrics(history, filter, 'Game Store');
    } else {
      setChartType(type || 'bar');
    }
  }

  const { trendTimeline, steamInfo, bottomGameStoreRenderList, loading } =
    useGameStorePcConsole({
      filter,
    });

  const seriesDataKeys = useMemo(() => getTableColumns(chartType), [chartType]);

  const { chartMap, chartNoDataMap } = usePcConsoleTimelineChart({
    platformData: trendTimeline,
    filterParams: filter,
    seriesDataKeys,
  });

  const history = useHistory();

  const records = useMemo(() => {
    const dateType = filter.date_type;
    const formatDate = formatDateFunc(dateType);
    const records: { [key in string]: any } = seriesDataKeys.reduce(
      (pre, { key, valueKey }) => {
        const selectedData = chartMap?.[key] || [];
        const value = arrayToSimpleObj(selectedData, valueKey, ({ time }) =>
          formatDate(time),
        );
        return { ...pre, [key]: value };
      },
      {},
    );

    const table: { [key in string]: any } = {};
    for (const key in records) {
      const element = records[key];
      Object.keys(element).forEach((time: string) => {
        if (!table[time]) table[time] = { time };

        table[time] = { ...table[time], [key]: element[time] };
      });
    }

    const list = Object.values(table);
    list.sort(sortTimeListFunc('time', 'desc'));

    return list;
  }, [chartMap, seriesDataKeys]);

  const leftEmpty = useMemo(() => {
    if (!steamInfo.all_reviews && !steamInfo.recent_reviews) return true;
    return (
      steamInfo?.all_reviews?.review === 0 &&
      steamInfo?.recent_reviews?.review === 0
    );
  }, [steamInfo]);

  const specialHeight = useMemo(() => bottomGameStoreRenderList.length === 0 ? 85 : 0, [bottomGameStoreRenderList]);

  function getChartElement(type: SymbolType) {
    if (type === 'table') {
      return (
        <MentionsTable
          seriesDataKeys={seriesDataKeys}
          filter={filter}
          records={records}
          specialHeight={specialHeight}
        />
      );
    }

    if (type === 'pie') {
      return (
        <MentionsPieCharts
          data={steamInfo}
          filter={filter}
          specialHeight={specialHeight}
        ></MentionsPieCharts>
      );
    }

    return (
      <MentionsLineBarCharts
        chartMap={chartMap}
        filter={filter}
        seriesDataKeys={seriesDataKeys}
        chartType={chartType}
        pieData={steamInfo}
        specialHeight={specialHeight}
      ></MentionsLineBarCharts>
    );
  }

  return (
    <CardCommon
      className="card-common-dbo-key-metrics margin-t-20 margin-b-10"
      hasDownload={!chartNoDataMap}
      operations={[
        vsIcon,
        pieIcon,
        tableIcon,
        lineIcon,
        stackLineIcon,
        barIcon,
      ]}
      operationInfo={[chartType, changeChartType]}
      handleDownloadOnClick={() => {
        downloadAction({
          key: 'KeyMetricsGameStoreSteam',
          fileName: 'Key Metrics Steam Game Store',
          filter,
          auxiliaryParams: {
            tableColumns: getTableColumns('table'),
          },
          data: records,
        });
      }}
      logBtnId="KMGS_PC"
      // empty={chartNoDataMap}
      // emptyHeight={378}
      loading={loading}
      loadingHeight={378}
    >
      <Row className="dbo-metrics-gamestore-box">
        <Col span={6}>
          <div className="dbo-metrics-gamestore-box-left" style={{ transform: 'translateY(-30px)' }}>
            {/* 组件来源 home/page/studio/opinions/GameStore*/}
            <ReviewsProgress
              data={steamInfo.all_reviews || getDefaultReview()}
              title="All Reviews"
            ></ReviewsProgress>
            <ReviewsProgress
              data={steamInfo.recent_reviews || getDefaultReview()}
              title="Recent Reviews"
            ></ReviewsProgress>
          </div>
        </Col>
        <Col span={18}>
          <div className="dbo-metrics-gamestore-box-right">
            <div className="dbo-metrics-gamestore-ratings-box">
              <CardCommon
                contentCard
                loading={loading}
                empty={chartNoDataMap}
                emptyHeight={268}
                emptyTitleText="No Steam data yet"
                emptyDescText="No Steam data under current filter selections"
              >
                {getChartElement(chartType)}
              </CardCommon>

              {/* 组件来源 home/page/studio/opinions/GameStore*/}
              {!(leftEmpty && chartNoDataMap) && (
                <Ratings renderList={bottomGameStoreRenderList} />
              )}
            </div>
          </div>
        </Col>
      </Row>
    </CardCommon>
  );
}
